if (!customElements.get("pack-builder")) {
  customElements.define(
    "pack-builder",
    class PackBuilder extends HTMLElement {
      connectedCallback() {
        if (this._initialized) return;
        this._initialized = true;

        this.product = this.parseProductJson();
        this.packQty = Number(this.dataset.packQty || 2);
        this.discountCents = Number(this.dataset.discountCents || 0);
        this.packHandle = this.dataset.packHandle || "";
        this.moneyFormat =
          this.dataset.moneyFormat ||
          window.FoxThemeSettings?.money_format ||
          "${{amount}}";

        this.slots = [...this.querySelectorAll("[data-pack-slot]")];
        this.atcBtn = this.querySelector("[data-pack-atc]");
        this.errorEl = this.querySelector("[data-pack-error]");
        this.cartDrawer = document.querySelector("cart-drawer");
        this.mainCart = document.querySelector("cart-items");

        this.selections = this.slots.map(() => null);
        this.slotTouched = this.slots.map(() => false);

        this.hideStickyAtc();
        this.bindEvents();
        this.slots.forEach((slot, index) => {
          const summary = slot.querySelector("[data-pack-slot-summary]");
          if (summary) summary.textContent = "";
          this.refreshSlot(index);
        });
        this.updatePrices();
        this.updateAtcState();
      }

      isSlotConfirmed(index) {
        return this.slots[index]?.classList.contains("is-confirmed");
      }

      /** Title case: first letter of each word, never ALL CAPS */
      toTitleCase(text) {
        if (!text) return "";
        return String(text)
          .toLowerCase()
          .replace(/\b([a-z])/g, (m) => m.toUpperCase());
      }

      formatSlotSummary(options, variant) {
        if (options?.length) {
          return options
            .map((o) => o.value)
            .filter(Boolean)
            .map((v) => this.toTitleCase(v))
            .join(" / ");
        }
        return variant?.title ? this.toTitleCase(variant.title) : "";
      }

      parseProductJson() {
        const el = this.querySelector("[data-pack-product-json]");
        if (!el) return { variants: [], options: [], price: 0 };
        try {
          return JSON.parse(el.textContent);
        } catch (e) {
          console.error("Pack Builder: invalid product JSON", e);
          return { variants: [], options: [], price: 0 };
        }
      }

      hideStickyAtc() {
        document
          .querySelectorAll(".sticky-atc-bar, sticky-atc-bar")
          .forEach((el) => {
            const bar = el.classList?.contains("sticky-atc-bar")
              ? el
              : el.closest(".sticky-atc-bar");
            if (bar) bar.classList.add("pack-builder-hide-sticky");
          });
      }

      bindEvents() {
        this.addEventListener("change", (e) => {
          const input = e.target.closest('input[type="radio"]');
          if (!input) return;
          const slot = input.closest("[data-pack-slot]");
          if (!slot) return;
          const index = Number(slot.dataset.slotIndex);
          this.slotTouched[index] = true;
          this.refreshSlot(index);
          this.updateAtcState();
        });

        this.addEventListener("click", (e) => {
          const toggle = e.target.closest("[data-pack-slot-toggle]");
          if (toggle) {
            const slot = toggle.closest("[data-pack-slot]");
            if (!slot || slot.classList.contains("is-locked")) return;
            const index = Number(slot.dataset.slotIndex);
            if (slot.classList.contains("is-open")) {
              this.closeSlot(index);
            } else {
              this.openSlot(index);
            }
            return;
          }

          const nextBtn = e.target.closest("[data-pack-next]");
          if (nextBtn) {
            e.preventDefault();
            const slot = nextBtn.closest("[data-pack-slot]");
            const index = Number(slot.dataset.slotIndex);
            this.confirmSlot(index);
            return;
          }

          if (e.target.closest("[data-pack-atc]")) {
            e.preventDefault();
            this.addPackToCart();
          }
        });
      }

      getSelectedOptions(slot) {
        const defaultId = slot.querySelector("[data-default-variant-id]");
        if (defaultId) {
          return { defaultVariantId: Number(defaultId.value) };
        }

        const options = [];
        slot.querySelectorAll("[data-option-name]").forEach((field) => {
          const checked = field.querySelector('input[type="radio"]:checked');
          const name = field.dataset.optionName;
          const value = checked ? checked.value : null;
          const labelEl = field.querySelector("[data-option-selected-label]");
          if (labelEl) {
            labelEl.textContent = value ? this.toTitleCase(value) : "";
          }
          options.push({ name, value });
        });
        return { options };
      }

      findVariant(selected) {
        if (selected.defaultVariantId) {
          return this.product.variants.find(
            (v) => v.id === selected.defaultVariantId
          );
        }
        if (!selected.options || selected.options.some((o) => !o.value)) {
          return null;
        }
        return this.product.variants.find((variant) =>
          selected.options.every((opt, i) => {
            const key = `option${i + 1}`;
            return variant[key] === opt.value;
          })
        );
      }

      refreshSlot(index) {
        const slot = this.slots[index];
        if (!slot) return;

        const selected = this.getSelectedOptions(slot);
        const variant = this.findVariant(selected);
        const summary = slot.querySelector("[data-pack-slot-summary]");
        const error = slot.querySelector("[data-pack-slot-error]");

        this.updateOptionAvailability(slot, selected);

        if (variant && variant.available) {
          this.selections[index] = variant;
          if (summary) {
            summary.textContent = this.slotTouched[index]
              ? this.formatSlotSummary(selected.options, variant)
              : "";
          }
          if (error) {
            error.hidden = true;
            error.textContent = "";
          }
          slot.classList.remove("is-invalid");
        } else {
          this.selections[index] = null;
          slot.classList.remove("is-confirmed");
          if (summary) {
            if (this.slotTouched[index] && selected.options) {
              summary.textContent = this.formatSlotSummary(selected.options);
            } else {
              summary.textContent = "";
            }
          }
          if (variant && !variant.available) {
            if (error) {
              error.hidden = false;
              error.textContent = "This combination is sold out.";
            }
            slot.classList.add("is-invalid");
          } else {
            if (error) {
              error.hidden = true;
              error.textContent = "";
            }
            slot.classList.remove("is-invalid");
          }
        }
      }

      updateOptionAvailability(slot, selected) {
        if (!selected.options) return;

        selected.options.forEach((opt, optionIndex) => {
          const field = slot.querySelector(
            `[data-option-index="${optionIndex}"]`
          );
          if (!field) return;

          field.querySelectorAll('input[type="radio"]').forEach((input) => {
            // Only constrain by options that already have a value.
            // Unset options must not mark everything sold-out (broke selection
            // after we removed default checked radios on first load).
            const testOptions = selected.options.map((o, i) =>
              i === optionIndex ? { ...o, value: input.value } : o
            );
            const match = this.product.variants.find((variant) =>
              testOptions.every((o, i) => {
                if (!o.value) return true;
                return variant[`option${i + 1}`] === o.value;
              })
            );
            const available = !!(match && match.available);
            input.classList.toggle("disabled", !available);
            input.disabled = false;
            const wrap = input.closest(".variant-picker__option");
            if (wrap) {
              wrap.classList.toggle(
                "variant-picker__option--soldout",
                !available
              );
            }
          });
        });
      }

      confirmSlot(index) {
        this.slotTouched[index] = true;
        this.refreshSlot(index);
        const variant = this.selections[index];
        const slot = this.slots[index];
        const error = slot.querySelector("[data-pack-slot-error]");

        if (!variant) {
          if (error) {
            error.hidden = false;
            error.textContent = "Please select available Color and Size.";
          }
          slot.classList.add("is-invalid");
          return;
        }

        slot.classList.add("is-confirmed");
        slot.classList.remove("is-locked", "is-invalid");

        const nextIndex = index + 1;
        if (nextIndex < this.slots.length) {
          this.slots[nextIndex].classList.remove("is-locked");
          this.openSlot(nextIndex);
        } else {
          this.closeSlot(index);
          this.updateAtcState();
          this.atcBtn?.focus();
        }
        this.updateAtcState();
      }

      openSlot(index) {
        this.slots.forEach((slot, i) => {
          if (i === index) {
            slot.classList.add("is-open", "is-current");
            const body = slot.querySelector("[data-pack-slot-body]");
            const toggle = slot.querySelector("[data-pack-slot-toggle]");
            if (body) body.hidden = false;
            if (toggle) toggle.setAttribute("aria-expanded", "true");
          } else {
            this.closeSlot(i);
          }
        });
      }

      closeSlot(index) {
        const slot = this.slots[index];
        if (!slot) return;
        slot.classList.remove("is-open", "is-current");
        const body = slot.querySelector("[data-pack-slot-body]");
        const toggle = slot.querySelector("[data-pack-slot-toggle]");
        if (body) body.hidden = true;
        if (toggle) toggle.setAttribute("aria-expanded", "false");
      }

      updatePrices() {
        const unit =
          this.selections.find((v) => v)?.price || this.product.price || 0;
        const compare = unit * this.packQty;
        const sale = Math.max(0, compare - this.discountCents);
        const money = (cents) =>
          typeof formatMoney === "function"
            ? formatMoney(cents, this.moneyFormat)
            : `$${(cents / 100).toFixed(2)}`;

        this.querySelectorAll("[data-pack-sale-price]").forEach((el) => {
          el.textContent = money(sale);
        });
        this.querySelectorAll("[data-pack-compare-price] s").forEach((el) => {
          el.textContent = money(compare);
        });
        this.querySelectorAll("[data-pack-save-amount]").forEach((el) => {
          el.textContent = money(this.discountCents);
        });
      }

      updateAtcState() {
        this.updatePrices();
        const ready =
          this.slots.length === this.packQty &&
          this.slots.every(
            (_, i) => this.isSlotConfirmed(i) && this.selections[i]?.available
          );
        if (this.atcBtn) this.atcBtn.disabled = !ready;
      }

      createPackKey() {
        return `PK-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      }

      async addPackToCart() {
        if (!this.atcBtn || this.atcBtn.disabled) return;

        const packKey = this.createPackKey();
        const items = this.selections.map((variant, index) => ({
          id: variant.id,
          quantity: 1,
          properties: {
            _PackHandle: this.packHandle,
            _PackKey: packKey,
            _PackSlot: String(index + 1),
            _PackDiscountCents: String(this.discountCents),
          },
        }));

        let sectionToBundle = [];
        document.documentElement.dispatchEvent(
          new CustomEvent("cart:grouped-sections", {
            bubbles: true,
            detail: { sections: sectionToBundle },
          })
        );

        if (this.cartDrawer) {
          sectionToBundle = [
            ...sectionToBundle,
            ...this.cartDrawer.getSectionsToRender().map((s) => s.id),
          ];
        }
        if (this.mainCart) {
          sectionToBundle = [
            ...sectionToBundle,
            ...this.mainCart.getSectionsToRender().map((s) => s.section),
          ];
        }

        const config = {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/javascript",
          },
          body: JSON.stringify({
            items,
            sections: sectionToBundle,
            sections_url: window.location.pathname,
          }),
        };

        this.atcBtn.classList.add("btn--loading");
        this.clearError();

        try {
          const response = await fetch(
            `${window.FoxThemeSettings.routes.cart_add_url}`,
            config
          );
          const data = await response.json();

          if (data.status) {
            this.showError(
              data.description || "Failed to add pack items to cart."
            );
            document.dispatchEvent(
              new CustomEvent("product-ajax:error", {
                detail: { errorMessage: data.description },
              })
            );
            return;
          }

          if (
            !window.FoxThemeSettings.cart.openDrawerWhenAdded ||
            window.FoxThemeSettings.cart.cartType === "page"
          ) {
            window.FoxTheme?.Notification?.show({
              target: this.errorEl || document.body,
              method: "appendChild",
              type: "success",
              message: window.FoxThemeStrings?.item_added || "Added to cart",
              last: 3000,
            });
          }

          if (window.FoxThemeSettings.cart.cartType === "page") {
            FoxThemeCartHelpers?.getState?.().then((state) => {
              FoxThemeCartHelpers.updateCartCount(state.item_count);
            });
          }

          if (this.cartDrawer && data.sections) {
            this.cartDrawer.renderContents(data);
          }

          window.FoxThemeEvents?.emit?.(PUB_SUB_EVENTS.cartUpdate, data);
          document.dispatchEvent(
            new CustomEvent("product-ajax:added", {
              detail: { product: data },
            })
          );
        } catch (err) {
          this.showError(err?.toString?.() || "Add to cart failed.");
        } finally {
          this.atcBtn.classList.remove("btn--loading");
        }
      }

      showError(message) {
        if (!this.errorEl) return;
        if (window.FoxTheme?.Notification) {
          window.FoxTheme.Notification.show({
            target: this.errorEl,
            method: "appendChild",
            type: "warning",
            message,
            last: 4000,
          });
        } else {
          this.errorEl.textContent = message;
        }
      }

      clearError() {
        if (this.errorEl) this.errorEl.textContent = "";
      }
    }
  );
}
